import { Button, useMediaQuery, Theme, TextField, InputAdornment } from "@mui/material";
import UiFormField from "../../common/ui/UiFormField";
import UiFormControlSelection from "../../common/ui/UiFormControlSelection";
import { ERRORS } from "../../constants/user-info.const";
import { UiText } from "../../common/ui/UiText";
import { makeStyles } from "@mui/styles";
import { toRem16 } from "../../../scss/utils/styles";
import Loader from "../../common/Loader";
import { ReactComponent as Lock } from "../../../assets/icons-svg/Lock.svg";
import { useRef, useState } from "react";
import { useJsApiLoader, StandaloneSearchBox } from "@react-google-maps/api";
import SearchIcon from "@mui/icons-material/Search";

const useStyles = makeStyles((theme: Theme) => {
    return {
        formContainer: {
            maxWidth: toRem16(480),
        },
        row: {
            marginBottom: "16px",
        },
        smallRow: {
            maxWidth: "260px",
            marginBottom: "16px",
            [theme.breakpoints.down("sm")]: {
                maxWidth: '100%',
            },
        },
        xsmallRow: {
            maxWidth: "260px",
            marginBottom: "16px",
            [theme.breakpoints.down("sm")]: {
                maxWidth: '70%',
            },
        },
        formInfo: {
            marginBottom: toRem16(24),
        },
        action: {
            margin: `${toRem16(40)} 0 ${toRem16(80)} 0`,
        },
        paymentInfp: {
            marginBottom: toRem16(24),
        },
        bankInputs: {
            maxWidth: toRem16(480),
        },
        secureText: {
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
        },
    };
});

interface Props {
    states: any;
    formValues: any;
    loading: boolean;
    setFieldValue: any;
}
enum ADDRESS_COMPONENTS {
    STREET_NUMBER = "street_number",
    ROUTE = "route",
    LOCALITY = "locality",
    STATE = "administrative_area_level_1",
    COUNTRY = "country",
    ZIP_CODE = "postal_code",
    COUNTY = "administrative_area_level_2",
}

const EmployeeForm = (props: Props) => {
    const ssnMask = "000-00-0000";
    const ssnDefinition = /[0-9]/;
    const smDevice = useMediaQuery("(max-width:600px)");
    const classes = useStyles();
    const autoCompleteRef = useRef<any>()
    const [formattedAddress, setFormattedAddress] = useState('')
    const { states, loading, setFieldValue } = props;

    const getStateAbbr = (stateName: string): string | null => {
        const state = states.find(
            (item: any) => item.name.toLowerCase() === stateName.toLowerCase()
        );
        return state ? state : null;
    };

    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: (window as any).ApiConfig.google_maps_api_key,
        libraries: ["places"],
    });
    const returnDatePicker = () => {
        if (!smDevice) {
            return (
                <UiFormControlSelection
                    label="Date of Birth (Required)"
                    type="date"
                    fieldName="date_of_birth"
                    dateType="string"
                    errorMessage={ERRORS.dob}
                    maxDate={new Date()}
                    showFloatingLabel={true}
                    placeholder="MM/DD/YYYY"
                    labelClass="date-picker"
                    isIsoDate={true}
                />
            );
        }
        return (
            <UiFormControlSelection
                label="Date of Birth (Required)"
                type="KeyboardDatePicker"
                fieldName="date_of_birth"
                dateType="string"
                errorMessage={ERRORS.dob}
                maxDate={new Date()}
                showFloatingLabel={true}
                dateFormat="MM/dd/yyyy"
                placeholder="MM/DD/YYYY"
                labelClass="date-picker"
            />
        );
    };
    return (
        <>
            <div className={classes.formContainer}>
                <div className={classes.formInfo}>
                    <UiText
                        typographyVariant="hatchback_125"
                        weight="semi_bold_600"
                    >
                        Personal Info
                    </UiText>
                </div>
                <div className={classes.row}>
                    <UiFormField
                        type="text"
                        placeholder="First Name (Required)"
                        label="First Name (Required)"
                        labelSize={false}
                        fieldName="first_name"
                        showFloatingLabel={true}
                    />
                </div>
                <div className={classes.smallRow}>
                    <UiFormField
                        type="text"
                        placeholder="Middle Name (Optional)"
                        label="Middle Name (Optional)"
                        labelSize={false}
                        fieldName="middle_initial"
                        showFloatingLabel={true}
                    />
                </div>

                <div className={classes.row}>
                    <UiFormField
                        type="text"
                        placeholder="Last Name (Required)"
                        label="Last Name (Required)"
                        fieldName="last_name"
                        labelSize={false}
                        showFloatingLabel={true}
                    />
                </div>
                <div className={classes.row}>
                    <UiFormField
                        type="text"
                        placeholder="Email (Required)"
                        label="Email (Required)"
                        labelSize={false}
                        fieldName="email"
                        showFloatingLabel={true}
                    />
                </div>
                <div className={classes.smallRow}>{returnDatePicker()}</div>
                <div className={classes.smallRow}>
                    <UiFormField
                        type="mask"
                        placeholder="Hidden"
                        label="Social Security Number"
                        fieldName="ssn"
                        mask={ssnMask}
                        definitions={ssnDefinition}
                        minLength={9}
                        labelSize={false}
                        showFloatingLabel={true}
                        helperText={
                            "For security purposes, your SSN has been hidden. You can update it by typing in the field."
                        }
                    />
                </div>
                <div className={classes.formInfo}>
                    <UiText
                        typographyVariant="hatchback_125"
                        weight="semi_bold_600"
                    >
                        Home Address
                    </UiText>
                </div>
                <div className={classes.row}>
                    {isLoaded && (
                        <StandaloneSearchBox
                            onLoad={(ref: any) => {
                                autoCompleteRef.current = ref;
                            }}
                            onPlacesChanged={() => {
                                const places =
                                    autoCompleteRef?.current?.getPlaces();
                                if (places.length) {
                                    const address = places[0];
                                    setFormattedAddress(
                                        address.formatted_address
                                    );

                                    const addressComponents =
                                        address.address_components;

                                    const addressComponentsMap =
                                        addressComponents.reduce(
                                            (acc: any, component: any) => {
                                                acc[component.types[0]] =
                                                    component.long_name;
                                                return acc;
                                            },
                                            {} as Record<string, string>
                                        );
                                    setFieldValue(
                                        "street_1",
                                        `${
                                            addressComponentsMap[
                                                ADDRESS_COMPONENTS.STREET_NUMBER
                                            ]
                                        } ${
                                            addressComponentsMap[
                                                ADDRESS_COMPONENTS.ROUTE
                                            ]
                                        }`
                                    );
                                    setFieldValue(
                                        "street_2",
                                        `${
                                            addressComponentsMap[
                                                "sublocality_level_1"
                                            ] ?? ""
                                        } `
                                    );
                                    setFieldValue(
                                        "city",
                                        addressComponentsMap[
                                            ADDRESS_COMPONENTS.LOCALITY
                                        ]
                                    );
                                    setFieldValue(
                                        "state",
                                        getStateAbbr(
                                            addressComponentsMap[
                                                ADDRESS_COMPONENTS.STATE
                                            ]
                                        )
                                    );
                                    setFieldValue(
                                        "zip",
                                        addressComponentsMap[
                                            ADDRESS_COMPONENTS.ZIP_CODE
                                        ]
                                    );
                                }
                            }}
                        >
                            <TextField
                                placeholder={"Search Location"}
                                variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                size="small"
                                fullWidth
                                value={formattedAddress}
                                onChange={(e) =>
                                    setFormattedAddress(e.target.value)
                                }
                            />
                        </StandaloneSearchBox>
                    )}
                </div>
                <div className={classes.row}>
                    <UiFormField
                        type="text"
                        placeholder="Street (Required)"
                        label="Street (Required)"
                        labelSize={false}
                        fieldName="street_1"
                        showFloatingLabel={true}
                    />
                </div>
                <div className={classes.row}>
                    <UiFormField
                        type="text"
                        placeholder="Street 2 (Optional)"
                        label="Street 2 (Optional)"
                        labelSize={false}
                        fieldName="street_2"
                        showFloatingLabel={true}
                    />
                </div>
                <div className={classes.row}>
                    <UiFormField
                        type="text"
                        placeholder="City (Required)"
                        label="City (Required)"
                        labelSize={false}
                        fieldName="city"
                        showFloatingLabel={true}
                    />
                </div>
                <div className={classes.xsmallRow}>
                    <UiFormControlSelection
                        type="autoComplete"
                        label={"State (Required)"}
                        showFloatingLabel={true}
                        fieldName="state"
                        optionsData={states}
                        optionValue={"name"}
                        optionKey={"id"}
                    />
                </div>
                <div className={classes.xsmallRow}>
                    <UiFormField
                        type="text"
                        placeholder="Zip Code (Required)"
                        label="Zip Code (Required)"
                        labelSize={false}
                        fieldName="zip"
                        showFloatingLabel={true}
                    />
                </div>
            </div>
            <div className={classes.formInfo}>
                <UiText
                    typographyVariant="hatchback_125"
                    weight="semi_bold_600"
                >
                    Payment Method
                </UiText>
                <div>
                    <UiText>
                        Your paycheck will be deposited to the provided bank account.
                    </UiText>
                </div>
            </div>
                <div className={classes.bankInputs}>
                    <div className={classes.row}>
                        <UiFormField
                            type="text"
                            placeholder="Bank Name (Required)"
                            label="Bank Name (Required)"
                            labelSize={false}
                            fieldName="bank_name"
                            showFloatingLabel={true}
                        />
                    </div>
                    <div className={classes.row}>
                        <UiFormField
                            type="text"
                            placeholder="Routing Number (Required)"
                            label="Routing Number (Required)"
                            labelSize={false}
                            fieldName="routing_number"
                            showFloatingLabel={true}
                        />
                    </div>
                    <div className={classes.row}>
                        <UiFormField
                            type="text"
                            placeholder="Account Number (Required)"
                            label="Account Number (Required)"
                            labelSize={false}
                            fieldName="account_number"
                            showFloatingLabel={true}
                        />
                    </div>
                    <div className={classes.secureText}>
                        <Lock />
                        <UiText typographyVariant="moped_75">
                            Your data is kept secure and private
                        </UiText>
                    </div>
                </div>
            <div className={classes.action}>
                {loading && <Loader />}
                <Button
                    variant="contained"
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                >
                    Send to Employer
                </Button>
            </div>
        </>
    );
};

export default EmployeeForm;
